import React, { lazy, Suspense } from 'react';
import { isGrouped } from '../utils';
import { Checkbox, Icon, Input, InputNumber, Radio, Select } from 'antd';
import { Switch, DatePicker } from 'Components/nui';
import 'antd/lib/tree-select/style/index.css';
export { default as RichTextWidget } from './RichTextWidget';

// Widgets are replaceble part as render of Fields.
// Although having same interface, widget is mainly used as function instead of as component,
//  for flexibility and compatibility (for those requiring class-based component).

const LazyTreeSelect = lazy(() => import('./TreeSelect'));

const TreeSelect = React.forwardRef((props, _) => {
  return (
    <Suspense fallback={null}>
      <LazyTreeSelect {...props} />
    </Suspense>
  );
});

export function SelectWidget({ props, choices, type, initialValue }) {
  if (props?.treeSelect) {
    return (
      <TreeSelect
        props={props}
        choices={choices}
        type={type}
        initialValue={initialValue}
      />
    );
  }
  return (
    <Select
      filterOption
      optionFilterProp="children"
      showSearch
      mode={type === 'MultipleSelect' ? 'multiple' : ''}
      dropdownMatchSelectWidth={false}
      getPopupContainer={trigger => trigger.parentNode}
      style={{ width: '100%' }}
      size="large"
      suffixIcon={<Icon type="caret-down" />}
      {...props}
    >
      {isGrouped(choices)
        ? choices.map(group => (
            <Select.OptGroup label={group.label} key={group.key}>
              {group.value.map(item => (
                <Select.Option
                  key={item.key}
                  value={item.value}
                  className={item.className}
                >
                  {item.label}
                </Select.Option>
              ))}
            </Select.OptGroup>
          ))
        : choices.map(item => (
            <Select.Option
              key={item.key}
              value={item.value}
              className={item.className}
            >
              {item.label}
            </Select.Option>
          ))}
    </Select>
  );
}

// The purpose of this widget is to pass accepted props to InputNumber
export function InputNumberWidget({ props, min, max, step }) {
  return (
    <InputNumber
      min={min * 1}
      max={max * 1}
      step={step}
      size="large"
      {...props}
    />
  );
}

export function RadioWidget({ props, choices }) {
  return (
    <Radio.Group {...props}>
      {choices.map(({ key, value, label }) => (
        <Radio key={key} value={value}>
          {label}
        </Radio>
      ))}
    </Radio.Group>
  );
}

export function RadioButtonWidget({ props, choices }) {
  return (
    <Radio.Group buttonStyle="solid" {...props}>
      {choices.map(({ key, value, label }) => (
        <Radio.Button key={key} value={value}>
          {label}
        </Radio.Button>
      ))}
    </Radio.Group>
  );
}

export function TextAreaWidget({ props }) {
  return (
    <Input.TextArea
      autoSize={{ minRows: 2, maxRows: 5 }}
      size="large"
      {...props}
    />
  );
}

export function InputWidget({ props }) {
  return <Input size="large" {...props} />;
}

export function DateTimeWidget({ props, initialValue, fromDate, ...rest }) {
  return (
    <DatePicker
      // react-datepicker uses date-fns format which uses XXX for z
      dateFormat={'d MMM yyyy HH:mm XXX'}
      minDate={fromDate}
      futureTimeOnly
      size="large"
      monthsShown={1}
      selectsRange={false}
      showTimeSelect
      timeIntervals={props?.showTime?.minuteStep}
      initialValue={initialValue}
      {...props}
    />
  );
}

// possibly not used
export function DateTimeRangeWidget({ props, fromDateTime, initialValue }) {
  return (
    <DatePicker
      // react-datepicker uses date-fns format which uses XXX for z
      dateFormat={'d MMM yyyy HH:mm XXX'}
      minDate={fromDateTime}
      size="large"
      initialValue={initialValue}
      timeIntervals={props?.showTime?.minuteStep}
      selectsRange
      showTimeSelect
      {...props}
    />
  );
}

export function DateWidget({ props, fromDate, initialValue }) {
  return (
    <DatePicker
      dateFormat={'d MMMM yyyy'}
      size="large"
      minDate={fromDate}
      monthsShown={1}
      selectsRange={false}
      initialValue={initialValue}
      {...props}
    />
  );
}

export function DateRangeWidget({ props, fromDate, initialValue }) {
  return (
    <DatePicker
      dateFormat={'d MMMM yyyy'}
      minDate={fromDate}
      size="large"
      initialValue={initialValue}
      {...props}
    />
  );
}

// TODO Checkbox for boolean, CheckboxGroup
export function CheckboxWidget({ initialValue, props }) {
  return <Checkbox defaultChecked={initialValue} {...props} />;
}

export function SwitchWidget({ initialValue, props }) {
  return <Switch defaultChecked={initialValue} {...props} />;
}

export function FileWidget({ fileRef, multiple = true, props }) {
  return <input multiple={multiple} ref={fileRef} {...props} type="file" />;
}
